import React, { useCallback, useEffect, useState } from 'react'
import queryString from 'query-string'
import classnames from 'classnames'
import Tooltip from 'antd/lib/tooltip'

import cfg from 'config'
import { apiManager, constants } from 'helpers'
import EventCard from './components/EventCard'
import EventsTabPlaceholder from './components/EventsTabPlaceholder'
import PartnerPortalPage from './components/PartnerPortalPage'
import cls from './PartnerPortal.module.scss'
import stripeLogo from 'assets/images/stripeLogo.png'

const {
  ROUTES: { EVENT_CREATE },
} = constants
const EVENTS_SHOWN = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  PENDING: 'pending',
  DELETED: 'deleted',
}
const { ACTIVE, COMPLETED, PENDING, DELETED } = EVENTS_SHOWN

const createEventButton = ({ showCreateEventPage }) => (
  <button
    className={cls.createEventButton}
    onClick={showCreateEventPage}
    data-cy="create-new-event-btn"
  >
    New Event
  </button>
)

const eventTab = (
  title,
  events,
  eventType,
  selectedEventType,
  clickEventTab,
  disableClickWhenNoEvents
) => (
  <button
    disabled={disableClickWhenNoEvents && !events.length}
    className={classnames(
      cls.tab,
      selectedEventType === eventType ? cls.activeTab : cls.inactiveTab
    )}
    onClick={() => clickEventTab(eventType)}
  >
    {title} ({events.length})
  </button>
)

const StripePayoutButton = ({ onClick, buttonText, stripeInfoIsFilledIn }) => (
  <div className="md:w-1/5 sm:w-2/5 h-12 m-4 mb-12 w-2/5">
    <button
      onClick={onClick}
      className={classnames(
        'w-full h-full rounded-md text-white bg-black text-sm flex items-center px-2',
        {
          'justify-between': !stripeInfoIsFilledIn,
        }
      )}
    >
      <img
        alt="stripe"
        src={stripeLogo}
        className={classnames('h-6 w-6', {
          'md:mr-10 mr-2': stripeInfoIsFilledIn,
        })}
      />
      {buttonText}
      {!stripeInfoIsFilledIn && (
        <span
          style={{ backgroundColor: '#FC3D39' }}
          className="h-5 w-5 rounded-2xl text-xs items-end justify-center"
        >
          1
        </span>
      )}
    </button>
  </div>
)

const PartnerPortalHomePage = ({ user, history }) => {
  const [loadedEvents, setLoadedEvents] = useState(false)
  const [placeholderTitle, setPlaceholderTitle] = useState('')
  const [stripeCheckIsDone, setStripeCheckIsDone] = useState(false)
  const [stripeCheck, setStripeCheck] = useState(false)
  const [stripeDashboardLink, setStripeDashboardLink] = useState()
  const [showStripeButton, setShowStripeButton] = useState(false)
  const [stripeUrl, setStripeUrl] = useState()

  const [eventLists, setEventLists] = useState({
    activeEvents: [],
    deletedEvents: [],
    expiredEvents: [],
    pendingEvents: [],
  })
  const [selectedEventType, selectEventType] = useState(ACTIVE)

  const getPartnerPortalEvents = useCallback(async () => {
    if (user?.id) {
      const eventLists = await apiManager.getPartnerPortalEvents()
      const { expiredEvents } = eventLists
      setEventLists(eventLists)
      setPlaceholderTitle(
        expiredEvents.length ? 'Create your next event' : 'Create your first event'
      )
      setLoadedEvents(true)
    }
  }, [user])

  const checkIfCompletedPartnerChecklist = useCallback(() => {
    setShowStripeButton(stripeCheckIsDone)
  }, [stripeCheckIsDone])

  const generateStripeOnboardingLink = useCallback(() => {
    const client_id = cfg.stripe.connect_key
    const state = Math.random().toString(36).slice(2)
    const stripeParameters = {
      redirect_uri: `${cfg.main_domain}/token`,
      client_id,
      state,
      'stripe_user[email]': user?.email,
      'stripe_user[phone_number]': user?.phoneNumber,
    }
    const stripeURL = `https://connect.stripe.com/express/oauth/authorize?${queryString.stringify(
      stripeParameters
    )}`
    setStripeUrl(stripeURL)
  }, [user])

  const showCreateEventPage = () => history.push(EVENT_CREATE)

  const checkStripeDashboardLink = useCallback(async () => {
    if (user?.id) {
      try {
        const { dashboardLink } = await apiManager.getStripeID()
        setStripeCheck(!!dashboardLink)
        setStripeDashboardLink(dashboardLink)
        setStripeCheckIsDone(true)
        checkIfCompletedPartnerChecklist()
      } catch (e) {
        console.error(e)
      }
    }
  }, [user, checkIfCompletedPartnerChecklist])

  useEffect(() => {
    if (user?.id) {
      getPartnerPortalEvents()
      checkIfCompletedPartnerChecklist()
      generateStripeOnboardingLink()
      checkStripeDashboardLink()
    }
  }, [
    user,
    getPartnerPortalEvents,
    checkIfCompletedPartnerChecklist,
    generateStripeOnboardingLink,
    checkStripeDashboardLink,
  ])

  const clickEventTab = eventType => {
    if (selectedEventType !== eventType) {
      selectEventType(eventType)
    }
  }

  const eventCardRow = events =>
    events.map(event => (
      <EventCard
        key={event.id}
        clickedEventCard={() => history.push(`/portal/events/${event.id}`)}
        event={event}
        user={user}
      />
    ))

  const { activeEvents, deletedEvents, expiredEvents, pendingEvents } = eventLists
  return (
    <PartnerPortalPage>
      <div className={classnames(cls.analyticsPage, cls.pageContainer, cls.analyticsPageIsVisible)}>
        <div className={cls.tabContainer}>
          {createEventButton({ showCreateEventPage })}
          {showStripeButton && (
            <StripePayoutButton
              onClick={() => window.open(stripeCheck ? stripeDashboardLink : stripeUrl, '_blank')}
              buttonText={stripeCheck ? 'Payouts Account' : 'Connect Payouts'}
              stripeInfoIsFilledIn={stripeCheck}
            />
          )}
          <h1
            className={classnames(cls.myEventsSubtitle, cls.hostPortalSubtitle, cls.showOnDesktop)}
          >
            My Events
          </h1>
          {eventTab('Active', activeEvents, ACTIVE, selectedEventType, clickEventTab)}
          {!!expiredEvents.length &&
            eventTab('Completed', expiredEvents, COMPLETED, selectedEventType, clickEventTab)}
          <Tooltip placement="topLeft" title="These events are in review">
            {eventTab('Pending', pendingEvents, PENDING, selectedEventType, clickEventTab, true)}
          </Tooltip>
          {!!deletedEvents.length &&
            eventTab('Cancelled', deletedEvents, DELETED, selectedEventType, clickEventTab, true)}
        </div>
        {loadedEvents ? (
          <div className={cls.eventsContainer}>
            {selectedEventType === ACTIVE && !activeEvents.length && (
              <EventsTabPlaceholder title={placeholderTitle} onButtonClick={showCreateEventPage} />
            )}
            {selectedEventType === ACTIVE && !!activeEvents.length && eventCardRow(activeEvents)}
            {selectedEventType === COMPLETED && eventCardRow(expiredEvents)}
            {selectedEventType === PENDING && eventCardRow(pendingEvents)}
            {selectedEventType === DELETED && eventCardRow(deletedEvents)}
          </div>
        ) : (
          <div className={classnames(cls.eventsContainer, 'text-center')}>
            Loading your events...
          </div>
        )}
      </div>
    </PartnerPortalPage>
  )
}

export default PartnerPortalHomePage
